import React from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  SelectChangeEvent,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { statusList } from "./task-constants";
import {
  inputLabelStyles,
  selectStyles,
  menuProps,
  chipStyles,
} from "./filter-styles";

interface StatusSelectProps {
  selectedStatuses: string[];
  onChange: (statuses: string[]) => void;
}

const StatusSelect: React.FC<StatusSelectProps> = ({
  selectedStatuses,
  onChange,
}) => {
  const allSelected = selectedStatuses.length === statusList.length;

  const handleStatusChange = (event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    const selectedValues = typeof value === "string" ? value.split(",") : value;

    // Check if "select-all" was clicked
    if (selectedValues.includes("select-all")) {
      // If all statuses are already selected, deselect all
      if (selectedStatuses.length === statusList.length) {
        onChange([]);
      } else {
        // Otherwise, select all
        onChange(statusList.map((s) => s.key));
      }
    } else {
      onChange(selectedValues);
    }
  };

  return (
    <FormControl fullWidth size="small">
      <InputLabel id="status-select-label" sx={inputLabelStyles}>
        Filter by Status
      </InputLabel>
      <Select
        labelId="status-select-label"
        id="status-select"
        multiple
        value={selectedStatuses}
        onChange={handleStatusChange}
        input={<OutlinedInput label="Filter by Status" />}
        data-testid="status-button-list"
        sx={selectStyles}
        MenuProps={menuProps}
        renderValue={(selected) => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {selected.length === statusList.length ? (
              <Chip label="All Statuses" size="small" sx={chipStyles} />
            ) : (
              selected.map((value) => {
                const status = statusList.find((s) => s.key === value);
                return (
                  <Chip
                    key={value}
                    label={status?.label}
                    size="small"
                    sx={chipStyles}
                  />
                );
              })
            )}
          </Box>
        )}
      >
        <MenuItem value="select-all">
          <Checkbox
            checked={allSelected}
            indeterminate={selectedStatuses.length > 0 && !allSelected}
          />
          <ListItemText
            primary="Select All"
            slotProps={{
              primary: { style: { fontWeight: 600 } },
            }}
          />
        </MenuItem>
        {statusList.map((status) => (
          <MenuItem key={status.key} value={status.key}>
            <Checkbox checked={selectedStatuses.includes(status.key)} />
            <ListItemText primary={status.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default StatusSelect;
